import { HTMLAttributes, memo, useMemo } from 'react';
import { useToggle } from 'ahooks';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import { useTranslate } from '@tolgee/react';
import {
  Button,
  Col,
  message,
  Popover,
  Row,
  Space,
  Table,
  TableColumnsType,
  TableColumnType,
  Typography,
} from 'antd';
import Icon, { DownOutlined, LinkOutlined, UpOutlined } from '@ant-design/icons';
import { formatNumber } from 'utils/numberFormat';
import { parse, stringify } from 'query-string';
import useIM from 'hooks/business/im/useIM';
import { ReactComponent as ForwardSvg } from 'assets/icons/forwardCurrentColor.svg';
import SellerCenter from 'components/SellerCenter';
import { getStoreCNSCInfoById } from 'services/api/store';
import toLowerCase from 'utils/toLowerCase';
import { lazadaSellerCenterHostMap, shopeeLocalSellerHostMap } from 'services/config/region';
import { localeCountryCode } from 'services/chattt';
import { ItemLink } from './TradeProduct';
import SkuCopy from './SkuCopy';
import { openExternalURL } from 'utils/openExternalURL';
import { useConversationReadonlyValue } from 'hooks/business/conversation';
import StockManage from './Item/StockManage';
import { QuickChatControl } from 'components/QuickChatControl';
import { isQuickChat } from 'productLine';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useXiapibuyEnable } from 'hooks/useXiapibuyEnable';
import Copy from './Copy';
import { ProductImages } from './ProductImages';
import { useIsERP } from 'hooks/business/useIsERP';
import { SkuStockStatus, SkuStockStatusTitle } from 'components/SkuStockStatus';
import { useIsSupportSendProduct } from 'hooks/business/chat/useChatSide';
import PreOrder from './Item/PreOrder';
import { isSupportSellerCenterURL } from 'utils/sellerCenterURL';
import { ReactComponent as CombineItemsSvg } from './assets/combineItems.svg';
import CombinedListingSkus, { CombinedListingSku } from './CombinedListingSkus';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

export function goItemSellerCenter(storeId: string, externalItemId: string, channel?: string) {
  if (channel === 'TOKOPEDIA') {
    openExternalURL(`https://seller.tokopedia.com/edit-product/${externalItemId}`, '__blank');
    return Promise.resolve();
  }

  return getStoreCNSCInfoById(storeId).then((res) => {
    const {
      data: {
        storeInfo: { externalType, region, storeType, externalStoreId },
      },
    } = res;

    if (externalType === 'SHOPEE') {
      if (storeType === 'CROSS') {
        openExternalURL(
          `https://seller.shopee.cn/portal/product/${externalItemId}/?cnsc_shop_id=${externalStoreId}`,
          '__blank',
        );
      } else {
        let lcc = 'CN';
        if (localeCountryCode) {
          lcc = localeCountryCode;
        }

        let host = `https://seller.shopee.co.${toLowerCase(region)}`;
        if (lcc !== 'CN') {
          const r = shopeeLocalSellerHostMap[toLowerCase(region)];
          if (r) {
            host = r;
          }
        }

        openExternalURL(`${host}/portal/product/${externalItemId}`, '__blank');
      }
    }

    if (externalType === 'LAZADA') {
      if (storeType === 'CROSS') {
        openExternalURL(
          `https://gsp.lazada-seller.cn/apps/gsp/product/publish?productId=${externalItemId}`,
          '__blank',
        );
      } else {
        const host = lazadaSellerCenterHostMap[region];

        if (host) {
          openExternalURL(`${host}/apps/product/publish?productId=${externalItemId}`, '__blank');
        }
      }
    }
  });
}

export interface ProductComponentProps extends HTMLAttributes<HTMLDivElement> {
  product: Conversation.Product;
  conversation: Conversation.Conversation;
  platform: General.PlatformUpperCaseTunnel;
}

function ProductComponent(props: ProductComponentProps) {
  const { product, platform, conversation, ...otherProps } = props;

  const {
    externalShopId,
    externalItemId,
    imageUrl = [],
    itemName: name,
    originalHighPrice: originalPrice,
    skuLowPrice: lowPrice,
    skuHighPrice: highPrice,
    unit = 'Rp',
    // sale,
    itemUrl,
    recentConsulting,
    skuInfo,
    itemSku,
    preOrderStatus,
  } = product;

  const readonly = useConversationReadonlyValue();

  const xiapibuyEnable = useXiapibuyEnable(conversation.region);

  const { conversationId, controlStatus, channel, isAffiliateSip, storeId } = conversation;

  const supportSend = useIsSupportSendProduct(channel);

  let url = itemUrl;

  if (channel === 'SHOPEE') {
    if (xiapibuyEnable && !itemUrl.includes('.xiapibuy.com')) {
      url = `https://${toLowerCase(
        conversation.region,
      )}.xiapibuy.com/product/${externalShopId}/${externalItemId}`;
    }
  }

  if (isTiKTokUpperCase(channel)) {
    if (!url) {
      url = `https://shop.tiktok.com/view/product/${externalItemId}?region=${conversation.region}`;
    } else {
      url = ensureTiktokProductUrlRegion(url, conversation.region);
    }
  }

  const [skuTableVisible, { toggle: toggleSkuTable }] = useToggle(false);
  const { sendItem } = useIM();
  const { t } = useTranslate();

  const sellerSKUSuffix = platform === 'LAZADA' ? '(Seller SKU)' : '';

  const isERP = useIsERP(storeId);

  const columns: TableColumnsType<Conversation.ProductSKUInfo> = useMemo(() => {
    const basic: TableColumnsType<Conversation.ProductSKUInfo> = [
      {
        title: 'SKU',
        dataIndex: 'sku',
        render: (_, { sku, isPreOrder }) => {
          return (
            <div className="flex items-center">
              {isPreOrder && <PreOrder preOrderStatus={preOrderStatus} />}
              <SkuCopy sku={sku} width={100} />
            </div>
          );
        },
      } as TableColumnType<Conversation.ProductSKUInfo>,
      {
        title: `${t('domain.product.field.skuCode')}${sellerSKUSuffix}`,
        dataIndex: 'sellerSku',
        render: (sku: string, { type, bundleItems }) => {
          if (type === 4 && bundleItems.length) {
            return (
              <Space>
                <SkuCopy sku={sku} width={100} />
                <Popover
                  arrowPointAtCenter
                  content={
                    <div style={{ maxWidth: '520px', margin: '-12px -16px' }}>
                      <CombinedListingSkus
                        combinedListingSkus={bundleItems.reduce<CombinedListingSku[]>(
                          (res, { spuInfo, skuInfo }) => {
                            const { itemName, itemUrl, imageUrl } = spuInfo;
                            return skuInfo.reduce((res, { skuCount, skuInfo }) => {
                              const { skuId, sellerSku, skuName = '' } = skuInfo;
                              res.push({
                                skuCount,
                                skuId,
                                sellerSku,
                                skuName,
                                title: itemName,
                                itemUrl,
                                image: imageUrl[0],
                              });
                              return res;
                            }, res);
                          },
                          [],
                        )}
                      />
                    </div>
                  }
                >
                  <Icon className="cursor-pointer" component={CombineItemsSvg} />
                </Popover>
              </Space>
            );
          }
          return <SkuCopy sku={sku} width={100} />;
        },
      },
    ];

    if (!isQuickChat) {
      return [
        ...basic,
        {
          title: <SkuStockStatusTitle isERP={isERP} />,
          dataIndex: 'skuStockStatus',
          width: 120,
          render: (_, record) => {
            return <SkuStockStatus skuInfo={record} isERP={isERP} />;
          },
        },
      ];
    }

    return basic;
  }, [sellerSKUSuffix, t, isERP, preOrderStatus]);

  const { whiteListConfig: { SLOW_PERFORMANCE_DEVICE } = {} } = useOrgConfigStateValue();

  const skuTable = useMemo(() => {
    return skuInfo?.length < 1 ? null : (
      <Table
        scroll={skuInfo?.length > 5 ? { y: 250 } : undefined}
        className={css`
          margin-top: 10px;
          border: 0 solid var(--fett-product-card-border);
          border-top-width: 1px;
          background: transparent;
          .ant-table {
            background: transparent;
          }
          .ant-table-thead > tr > th {
            background: var(--fett-bg-subtle);
          }
          tbody {
            tr:nth-of-type(2) {
              background: var(--fett-product-table-stripe);
              border: 1px solid var(--fett-product-card-border);
            }
          }
        `}
        rowKey="skuId"
        pagination={skuInfo.length > 20 && SLOW_PERFORMANCE_DEVICE ? { simple: true } : false}
        size="small"
        columns={columns}
        dataSource={skuInfo}
      />
    );
  }, [skuInfo, columns, SLOW_PERFORMANCE_DEVICE]);

  const primarySku = useMemo(() => {
    return platform === 'SHOPEE' ? (
      <SkuCopy width={250} sku={itemSku} label={`${t('domain.product.field.primarySkuCode')}:`} />
    ) : null;
  }, [itemSku, platform, t]);

  const priceRender = useMemo(() => {
    if (channel === 'LAZADA' && lowPrice === 0 && lowPrice === highPrice) {
      return <p>{`${unit} ${formatNumber(originalPrice, '0,0.00')}`}</p>;
    }

    return (
      <PriceWrapper align="center">
        <p>
          {lowPrice === highPrice
            ? `${unit} ${formatNumber(lowPrice, '0,0.00')}`
            : `${unit} ${formatNumber(lowPrice, '0,0.00')} - ${unit} ${formatNumber(
                highPrice,
                '0,0.00',
              )}`}
        </p>
        {originalPrice !== highPrice && (
          <del>{`${unit} ${formatNumber(originalPrice, '0,0.00')}`}</del>
        )}
      </PriceWrapper>
    );
  }, [channel, lowPrice, highPrice, originalPrice, unit]);

  return (
    <Container {...otherProps}>
      <ContentWrapper>
        <Content>
          <span>
            <ProductImages images={imageUrl} platform={platform} />
            {recentConsulting && (
              <b>{t('scene.chatWorkspace.productTab.label.recentConsultation')}</b>
            )}
          </span>
          <div onClick={() => openExternalURL(url, '_blank')}>
            <h4 title={name}>
              <PreOrder platform={channel} preOrderStatus={preOrderStatus} />
              <div>{name}</div>
              <Copy
                text={name}
                successMessage={t('common.feedback.copySuccess')}
                failedMessage={t('common.feedback.copyFailed')}
                onClick={(e) => e.stopPropagation()}
              />
            </h4>
            {priceRender}
            <ItemLink className="itme-link-icon">
              <LinkOutlined />
            </ItemLink>
          </div>
        </Content>
        <Row justify="space-between" align="middle">
          <Col span={16}>{primarySku}</Col>
          <Col>
            {skuTable && (
              <Space
                className={css`
                  cursor: pointer;
                  color: var(--fett-text-secondary);
                  .anticon {
                    color: var(--fett-text-tertiary);
                  }
                  &:hover {
                    color: var(--main-color);
                    .anticon {
                      color: inherit;
                    }
                  }
                `}
                align="center"
                onClick={() => {
                  toggleSkuTable();
                }}
              >
                <span>{t('scene.chatWorkspace.productTab.title.skuInfo')}</span>
                <div
                  className={css`
                    font-size: 10px;
                  `}
                >
                  {skuTableVisible ? <UpOutlined /> : <DownOutlined />}
                </div>
              </Space>
            )}
          </Col>
        </Row>
        {skuTableVisible && skuTable}
      </ContentWrapper>
      <Toolbar>
        {/* 缺货标记 */}
        <QuickChatControl>
          <StockManage product={product} conversation={conversation} />
        </QuickChatControl>
        <ButtonWrapper className="inline-flex items-center basis-auto overflow-hidden flex-grow justify-end">
          {isSupportSellerCenterURL(channel) ? (
            <SellerCenter platform={channel}>
              <Button
                className={css`
                  flex-basis: auto;
                  overflow: hidden;
                  display: inline-flex;
                  align-items: center;
                  & > span {
                    &:not(.anticon) {
                      flex-basis: auto;
                      overflow: hidden;
                      white-space: nowrap;
                      text-overflow: ellipsis;
                    }
                    &.anticon {
                      flex-shrink: 0;
                    }
                  }
                `}
                size="small"
                onClick={() => {
                  goItemSellerCenter(storeId, externalItemId, channel).catch(() => {
                    message.error(t('scene.chatWorkspace.orderTab.error.openSellerCenterFailed'));
                  });
                }}
              >
                <Typography.Text ellipsis={{ tooltip: true }}>
                  {t('scene.chatWorkspace.orderTab.action.viewSellerCenter')}
                </Typography.Text>
                <Icon component={ForwardSvg} />
              </Button>
            </SellerCenter>
          ) : null}
          {supportSend ? (
            <Button
              size="small"
              type="primary"
              className="flex-shrink-0"
              disabled={readonly || controlStatus === false || isAffiliateSip}
              onClick={() => {
                sendItem(conversationId, {
                  externalStoreId: externalShopId,
                  externalItemId,
                }).catch(() => {
                  message.error(t('scene.chatWorkspace.productTab.feedback.sendFailed'));
                });
              }}
            >
              {t('scene.chatWorkspace.productTab.action.send')}
            </Button>
          ) : null}
        </ButtonWrapper>
      </Toolbar>
    </Container>
  );
}

function ensureTiktokProductUrlRegion(originUrl: string, region: string) {
  try {
    const url = new URL(originUrl);
    const search = parse(url.search);
    if (!search?.region) {
      search.region = region;
      url.search = stringify(search);
    }
    return url.href;
  } catch (err) {
    return originUrl;
  }
}

const Container = styled.div`
  position: relative;
  background: var(--fett-product-card-bg);
  .${({ theme }) => theme.getAntdPrefixedClassName('tag')} {
    position: absolute;
    top: 0;
    left: 0;
    padding: 0 3px;
  }
`;
const ContentWrapper = styled.div`
  padding: 5px 10px;
  border-bottom: solid 1px var(--fett-product-card-border);
`;
const Content = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
  padding: 5px;
  cursor: pointer;
  > span {
    position: relative;
    flex: 0 0 48px;
    height: 48px;
    margin-right: 10px;
    /* overflow: hidden; */
    > b {
      position: absolute;
      font-weight: 500;
      left: 0;
      bottom: 0;
      width: 100%;
      line-height: 16px;
      font-size: 12px;
      color: #fff;
      background: var(--main-color);
      opacity: 0.8;
    }
  }
  > div {
    flex: 1 0 0;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    margin-top: 5px;
    overflow: hidden;
    > * {
      margin: 0;
      font-size: 12px;
    }
    > h4 {
      display: flex;
      align-items: center;
      margin-right: 12px;
      font-size: 14px;
      color: var(--fett-text-primary);
      overflow: hidden;
      > * {
        flex: 0 0 auto;
      }
      .ant-tag {
        border: none;
        color: var(--fett-scenario-success-text);
        margin-right: 2px;
        max-width: 40px;
        padding: 0 4px;
        font-size: 12px;
        flex-shrink: 0;
        align-items: center;
        position: initial;
      }
      div {
        flex-shrink: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 18px;
      }
    }
  }
  .itme-link-icon {
    display: none;
  }
  &:hover {
    background: var(--fett-product-card-hover);

    .itme-link-icon {
      display: block;
    }
  }
`;
const PriceWrapper = styled(Space)`
  font-size: 12px;
  padding: 4px 0;
  del {
    margin: 0;
    color: var(--fett-text-quaternary);
  }
  p {
    color: var(--fett-colors-price);
    margin: 0;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  overflow: hidden;
  background: var(--fett-product-card-bg);
`;

const ButtonWrapper = styled.div`
  .${({ theme }) => theme.getAntdPrefixedClassName('btn')} {
    &:active,
    &:visited {
      color: var(--fett-text-primary);
      border-color: var(--fett-border-strong);
    }
    &:hover,
    &:focus {
      color: var(--main-color);
      border-color: var(--main-color);
    }
    &-primary,
    &-primary:hover,
    &-primary:active,
    &-primary:focus,
    &-primary:visited {
      margin-left: 10px;
      background: var(--main-color);
      border-color: var(--main-color);
      color: #fff;
    }
    &-primary[disabled] {
      color: var(--fett-text-quaternary);
      border-color: var(--fett-border-default);
      background: var(--fett-fill-200);
    }
  }
`;

export default memo(ProductComponent);
